import type { SelectionRange } from "../global";
import { SelectionHandler, SelectionPhase } from "../office/SelectionHandler";
import { SelectionRangeFactory } from "../office/SelectionRangeFactory";
import { onOfficeReady } from "../office/state";
import Alpine from "alpinejs";
import { dispatch } from "../utils/events";
import { DEBOUNCE_EDITOR_SELECTION_MS, DEBOUNCE_EDITOR_SELECTION_MULTIPLIER } from "../timing";

export const selectionHandler = new SelectionHandler();

export type { SelectionPhase } from "../office/SelectionHandler";

export const selectionState = Alpine.reactive({
  value: "unregistered"
} as { value: SelectionPhase });


export function startSelectionObservation() {
  selectionHandler.resume();
}

export function stopSelectionObservation() {
  selectionHandler.pause();
}

export async function getCurrentSelectionRange(): Promise<SelectionRange | null> {
  return await selectionHandler.factory?.buildRange() || Promise.resolve(null);
}

export function getSelectionRangeFactory() {
  return selectionHandler.factory;
}

async function init() {
  const factory = new SelectionRangeFactory(Word.run);

  await selectionHandler.register({
    run: Word.run,
    debounceMs: DEBOUNCE_EDITOR_SELECTION_MS,
    debounceMultiplier: DEBOUNCE_EDITOR_SELECTION_MULTIPLIER,
    fetchSelection: (signal) => factory.buildRange(signal),
    factory,
  });

  selectionHandler.onPhaseChange((phase) => {
    selectionState.value = phase;
  });

  selectionHandler.onSelection((sel) => {
    if (sel !== null) {
      dispatch("document-selection-changed", sel);
    }
  });

  Office.context.document.addHandlerAsync(
    Office.EventType.DocumentSelectionChanged,
    () => selectionHandler.handleSelectionChanged()
  );
}

onOfficeReady(async (enabled) => {
  if (!enabled) return;
  if (Office.context.platform === Office.PlatformType.OfficeOnline) {
    console.warn("Selection observation is disabled on office web");
    return;
  }

  await init();
});
